import { createContext, useCallback, useContext, useId, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { Btn } from './controls';
import { tint, v } from './format';

/* ---- toasts ---------------------------------------------------------------------------- */

const ToastCtx = createContext<(msg: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [key, setKey] = useState(0);
  const show = useCallback((m: string) => { setMsg(m); setKey((k) => k + 1); }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <Snackbar
        key={key}
        open={msg !== null}
        autoHideDuration={3400}
        onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: 13.5 }}>
            <IconCheck size={16} stroke={2.6} color="#2FD6A6" />
            {msg}
          </Box>
        }
        action={<IconButton size="small" aria-label="Dismiss" onClick={() => setMsg(null)} sx={{ color: '#9AA3B7' }}><IconX size={14} /></IconButton>}
      />
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/* ---- dialogs --------------------------------------------------------------------------- */

export function AppDialog({
  open, onClose, title, children, confirmLabel, onConfirm, kind = 'primary', width = 460, cancelLabel = 'Cancel', confirmDisabled,
}: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; confirmLabel?: string; onConfirm?: () => void;
  kind?: 'primary' | 'danger'; width?: number; cancelLabel?: string; confirmDisabled?: boolean;
}) {
  const titleId = useId();
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId} slotProps={{ paper: { sx: { width, maxWidth: 'calc(100% - 32px)', m: 2 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '18px 22px', borderBottom: `1px solid ${v('border')}` }}>
        <Box component="h2" id={titleId} sx={{ m: 0, fontSize: 16, fontWeight: 600 }}>{title}</Box>
        <IconButton aria-label="Close" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '8px', background: v('sunk'), color: v('dim') }}><IconX size={16} /></IconButton>
      </Box>
      <Box sx={{ p: '20px 22px', display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', p: '16px 22px', borderTop: `1px solid ${v('border')}`, background: v('sunk') }}>
        <Btn onClick={onClose}>{cancelLabel}</Btn>
        {confirmLabel && (
          <Btn kind={kind === 'danger' ? 'solid-danger' : 'primary'} onClick={onConfirm} disabled={confirmDisabled}>{confirmLabel}</Btn>
        )}
      </Box>
    </Dialog>
  );
}

/** Right-hand review drawer (Reconciliation exceptions, Ops records). */
export function SideDrawer({ open, onClose, title, subtitle, children, footer, width = 460 }: { open: boolean; onClose: () => void; title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; width?: number }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width, maxWidth: '100%', borderLeft: `1px solid ${v('border2')}` } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: '18px 22px', borderBottom: `1px solid ${v('border')}` }}>
          <Box>
            <Box component="h2" sx={{ m: 0, fontSize: 16, fontWeight: 600 }}>{title}</Box>
            {subtitle && <Box sx={{ mt: '4px', fontSize: 12.5, color: v('caption') }}>{subtitle}</Box>}
          </Box>
          <IconButton aria-label="Close" onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '8px', background: v('sunk'), color: v('dim') }}><IconX size={16} /></IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: '20px 22px', display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
        {footer && <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', p: '16px 22px', borderTop: `1px solid ${v('border')}`, background: v('sunk') }}>{footer}</Box>}
      </Box>
    </Drawer>
  );
}

/* ---- inline messages ------------------------------------------------------------------- */

type Tone = 'success' | 'info' | 'warning' | 'danger';

export function Banner({ tone = 'success', icon, children, action }: { tone?: Tone; icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  const c = v(tone);
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '13px 16px', borderRadius: '12px', background: tint(c, 8), boxShadow: `0 0 0 1px ${tint(c, 30)} inset`, fontSize: 13.5, color: v('text') }}>
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', background: tint(c, 18), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: c }}>
        {icon ?? (tone === 'success' ? <IconCheck size={14} stroke={2.6} /> : tone === 'info' ? <IconInfoCircle size={15} /> : <IconAlertTriangle size={14} />)}
      </Box>
      <Box sx={{ flex: 1 }}>{children}</Box>
      {action}
    </Box>
  );
}

export function Note({ tone = 'info', icon, children }: { tone?: Tone; icon?: ReactNode; children: ReactNode }) {
  const c = v(tone);
  return (
    <Box sx={{ display: 'flex', gap: '10px', p: '12px 14px', borderRadius: '10px', background: tint(c, 8), fontSize: 12.5, color: v('dim'), lineHeight: 1.5 }}>
      <Box sx={{ display: 'flex', flexShrink: 0, color: c }}>{icon ?? <IconInfoCircle size={16} />}</Box>
      <Box>{children}</Box>
    </Box>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <Box sx={{ textAlign: 'center', p: '44px 20px' }}>
      <Box sx={{ fontSize: 15, fontWeight: 600, color: v('text') }}>{title}</Box>
      {body && <Box sx={{ mt: '6px', fontSize: 13, color: v('dim'), maxWidth: 440, mx: 'auto' }}>{body}</Box>}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}
